import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppAuth } from '../context/AuthContext';
import { usePanel } from '../context/PanelContext';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { components } from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useNotification } from '../context/NotificationContext';
import MINECRAFT_ITEMS from '../data/minecraftItems';
import { spriteMap, SPRITE_SIZE } from '../data/itemSpriteMap';
import './EventManagementPage.css';

const EMPTY_FORM = {
  name: '',
  description: '',
  time: null,
  item: '',
  server: 'EA',
  visibility: true,
  voicechat: false,
  slug: '',
  webAddon: '',
  webAddonConfig: '{"roundDurationSec":300,"intermissionSec":30}',
};

/* ── Item icon via sprite sheet ── */
function McIcon({ item, size = 24 }) {
  if (!item) return null;
  const pos = spriteMap[item];
  if (!pos) return null;
  const scale = size / SPRITE_SIZE;
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundImage: 'url(/img/item-sprites.png)',
        backgroundPosition: `-${pos[0] * scale}px -${pos[1] * scale}px`,
        backgroundSize: `${1248 * scale}px ${1248 * scale}px`,
        imageRendering: 'pixelated',
        flexShrink: 0,
      }}
    />
  );
}

/* ── Minecraft color code map ── */
const MC_COLORS = {
  '0': '#000000', '1': '#0000AA', '2': '#00AA00', '3': '#00AAAA',
  '4': '#AA0000', '5': '#AA00AA', '6': '#FFAA00', '7': '#AAAAAA',
  '8': '#555555', '9': '#5555FF', 'a': '#55FF55', 'b': '#55FFFF',
  'c': '#FF5555', 'd': '#FF55FF', 'e': '#FFFF55', 'f': '#FFFFFF',
};


/* Minecraft shadow = same color darkened, offset 2px right & down */
function mcShadow(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const d = (v) => Math.round(v * 0.5).toString(16).padStart(2, '0');
  return `#${d(r)}${d(g)}${d(b)}`;
}

/**
 * Parse Skript/Minecraft color codes into React spans.
 */
function parseSkriptColors(text, shadowPx = 2) {
  if (!text) return null;

  const spans = [];
  let color = '#FFFFFF';
  let bold = false;
  let italic = false;
  let underline = false;
  let buf = '';
  let i = 0;

  const flush = () => {
    if (buf) {
      spans.push(
        <span key={spans.length} style={{
          color,
          textShadow: `${shadowPx}px ${shadowPx}px 0px ${mcShadow(color)}`,
          fontWeight: bold ? 'bold' : 'normal',
          fontStyle: italic ? 'italic' : 'normal',
          textDecoration: underline ? 'underline' : 'none',
        }}>{buf}</span>
      );
      buf = '';
    }
  };

  while (i < text.length) {
    if (text[i] === '<' && text[i + 1] === '#' && i + 8 < text.length && text[i + 8] === '>') {
      flush();
      color = text.slice(i + 1, i + 8);
      i += 9;
      continue;
    }
    if (text[i] === '&' && i + 1 < text.length) {
      const code = text[i + 1].toLowerCase();
      if (MC_COLORS[code]) { flush(); color = MC_COLORS[code]; i += 2; continue; }
      if (code === 'l') { flush(); bold = true; i += 2; continue; }
      if (code === 'o') { flush(); italic = true; i += 2; continue; }
      if (code === 'n') { flush(); underline = true; i += 2; continue; }
      if (code === 'r') { flush(); color = '#FFFFFF'; bold = false; italic = false; underline = false; i += 2; continue; }
    }
    buf += text[i];
    i++;
  }
  flush();
  return spans.length > 0 ? spans : text;
}

/* ── MOTD Editor Component ── */
function MotdEditor({ value, onChange }) {
  const inputRef = useRef(null);
  const [color, setColor] = useState('#FFFFFF');

  const insertAtCursor = (code) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const next = value.slice(0, start) + code + value.slice(end);
    onChange(next);
    // Restore cursor after the inserted code
    setTimeout(() => {
      input.focus();
      const pos = start + code.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div className="motd-editor">
      <div className="motd-toolbar">
        <button type="button" className="motd-tool-btn motd-color-btn" onClick={() => insertAtCursor(`<${color}>`)} title="Insert color">
          <input
            type="color"
            value={color}
            onChange={(e) => { e.stopPropagation(); setColor(e.target.value.toUpperCase()); }}
            onClick={(e) => e.stopPropagation()}
            className="motd-color-input"
            title="Pick a color"
          />
          Insert Color
        </button>
        <button type="button" className="motd-tool-btn motd-bold-btn" onClick={() => insertAtCursor('&l')} title="Bold">
          Bold
        </button>
      </div>

      <input
        ref={inputRef}
        type="text"
        className="motd-text-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type event name with color codes..."
        maxLength={200}
        required
        autoFocus
      />

      {value && (
        <div className="motd-preview-bar">
          <div className="motd-preview">
            {parseSkriptColors(value)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── react-select custom components for item icons ── */
const ItemOption = (props) => (
  <components.Option {...props}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <McIcon item={props.data.value} size={24} />
      {props.data.label}
    </span>
  </components.Option>
);

const ItemSingleValue = (props) => (
  <components.SingleValue {...props}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <McIcon item={props.data.value} size={20} />
      {props.data.label}
    </span>
  </components.SingleValue>
);

/* ── react-select theme to match dark admin theme ── */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    fontSize: '0.95rem',
    border: `2px solid ${state.isFocused ? '#F5B041' : '#4d5260'}`,
    borderRadius: 10,
    background: state.isFocused ? '#3b3f47' : '#2b2d33',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(245, 176, 65, 0.15)' : 'none',
    minHeight: 42,
    '&:hover': { borderColor: state.isFocused ? '#F5B041' : '#5a5f6d' },
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: "'Nunito', sans-serif",
    fontWeight: 600,
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    background: state.isFocused ? 'rgba(245, 176, 65, 0.12)' : '#33363d',
    color: '#eceef1',
    cursor: 'pointer',
    '&:active': { background: 'rgba(245, 176, 65, 0.2)' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: 'hidden',
    background: '#33363d',
    border: '1px solid #4d5260',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 10,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 240,
  }),
  placeholder: (base) => ({
    ...base,
    color: '#5a5f6d',
    fontWeight: 600,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#eceef1',
  }),
  input: (base) => ({
    ...base,
    color: '#eceef1',
  }),
};

/* ── Hold-to-Delete Button ── */
function HoldToDeleteBtn({ onDelete, disabled }) {
  const [holding, setHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const HOLD_MS = 1200;

  const start = () => {
    if (disabled) return;
    setHolding(true);
    setProgress(0);
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.min(elapsed / HOLD_MS, 1);
      setProgress(pct);
      if (pct >= 1) {
        cancel();
        onDelete();
      }
    }, 30);
  };

  const cancel = () => {
    setHolding(false);
    setProgress(0);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <button
      type="button"
      className={`em-btn-delete ${holding ? 'em-btn-delete-holding' : ''}`}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onTouchStart={start}
      onTouchEnd={cancel}
      disabled={disabled}
      title="Hold to delete"
    >
      {holding && (
        <span className="em-delete-progress" style={{ width: `${progress * 100}%` }} />
      )}
      {holding ? 'Deleting...' : 'Delete'}
    </button>
  );
}

function toDateObject(epochSeconds) {
  if (!epochSeconds) return null;
  return new Date(epochSeconds * 1000);
}

function fromDateObject(date) {
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
}

// Preload sprite sheet immediately on module load
new Image().src = '/img/item-sprites.png';

export default function EventManagementPage() {
  const { user, minecraftUuid, minecraftUsername } = useAppAuth();
  const { events, loading, error, authFetch, refreshEvents } = usePanel();
  const { notify } = useNotification();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [mcProfile, setMcProfile] = useState(() => {
    if (minecraftUuid) return { uuid: minecraftUuid, username: minecraftUsername };
    // Check for freshly linked profile from Xbox callback
    try {
      const stored = sessionStorage.getItem('mc_linked_profile');
      if (stored) {
        sessionStorage.removeItem('mc_linked_profile');
        sessionStorage.removeItem('xbox_redirect_pending');
        return JSON.parse(stored);
      }
    } catch {}
    return { uuid: null, username: null };
  });
  const [linkingMc, setLinkingMc] = useState(false);

  useEffect(() => {
    if (minecraftUuid) {
      setMcProfile({ uuid: minecraftUuid, username: minecraftUsername });
      sessionStorage.removeItem('xbox_redirect_pending');
    }
  }, [minecraftUuid, minecraftUsername]);

  // Auto-link Minecraft: redirect to Xbox OAuth once if not linked
  useEffect(() => {
    if (!user || minecraftUuid || mcProfile.uuid) return;
    // Prevent redirect loop — only redirect once per session
    if (sessionStorage.getItem('xbox_redirect_pending')) return;

    sessionStorage.setItem('xbox_redirect_pending', '1');
    const state = crypto.randomUUID();
    sessionStorage.setItem('xbox_oauth_state', state);
    const params = new URLSearchParams({
      client_id: '42d27845-af7e-42ba-8c15-1ec7bcaca4b6',
      response_type: 'code',
      redirect_uri: `${window.location.origin}/xbox-callback`,
      scope: 'XboxLive.signin XboxLive.offline_access',
      state,
    });
    window.location.href = `https://login.microsoftonline.com/consumers/oauth2/v2.0/authorize?${params}`;
  }, [user, minecraftUuid]);

  const clearMessages = () => { setFormError(null); };

  const openCreateForm = () => {
    clearMessages();
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (event) => {
    clearMessages();
    setEditingId(event.id);
    setForm({
      name: event.name || '',
      description: (event.description || '').replace(/\/n/g, '\n'),
      time: toDateObject(event.time),
      item: event.item || '',
      server: 'EA',
      visibility: event.visibility ?? true,
      voicechat: event.voicechat ?? false,
      slug: event.slug || '',
      webAddon: event.webAddon || '',
      webAddonConfig: event.webAddonConfig || '{"roundDurationSec":300,"intermissionSec":30}',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();
    setSaving(true);
    try {
      const time = fromDateObject(form.time);
      if (!time) throw new Error('A valid date and time is required');
      const payload = {
        name: form.name.trim(),
        time,
        description: form.description.trim().replace(/\n/g, '/n') || undefined,
        item: form.item.trim() || undefined,
        server: 'EA',
        visibility: form.visibility,
        voicechat: form.voicechat,
        slug: form.slug.trim() || undefined,
        webAddon: form.webAddon || null,
        webAddonConfig: form.webAddon ? form.webAddonConfig : undefined,
      };
      if (editingId) {
        await authFetch('PUT', { id: editingId, ...payload });
        notify('Event updated successfully');
      } else {
        await authFetch('POST', payload);
        notify('Event created successfully');
      }
      closeForm();
      await refreshEvents();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setFormError(null);
    setDeleting(true);
    try {
      await authFetch('DELETE', { id });
      notify('Event deleted successfully');
      await refreshEvents();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleVisibility = (event) => {
    const newVis = !event.visibility;
    authFetch('PUT', { id: event.id, visibility: newVis })
      .then(() => refreshEvents())
      .catch((err) => {
        notify(err.message);
      });
  };

  const handleField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedItem = form.item
    ? MINECRAFT_ITEMS.find(i => i.value === form.item) || { value: form.item, label: form.item.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }
    : null;

  const now = Math.floor(Date.now() / 1000);
  const authReady = !!user;

  return (
    <section className="em-page">
      <div className="em-container">
        <div className="em-header">
          <h1 className="em-title">Event Management</h1>
        </div>

        {(error || formError) && <div className="em-alert em-alert-error">{formError || error}</div>}

        <div className="em-panel">
          {!authReady ? (
            <div className="em-events-list">
              {[0, 1].map((i) => (
                <div key={i} className="em-event-card em-skeleton-card">
                  <div className="em-skeleton-eye" />
                  <div className="em-event-info">
                    <div className="em-skeleton-title" />
                    <div className="em-skeleton-meta">
                      <div className="em-skeleton-line em-skeleton-date" />
                      <div className="em-skeleton-line em-skeleton-server" />
                    </div>
                  </div>
                  <div className="em-event-actions">
                    <div className="em-skeleton-btn" />
                    <div className="em-skeleton-btn em-skeleton-btn-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : (<>
          <div className="em-panel-header">
            <button className="em-btn-save" onClick={openCreateForm} disabled={showForm}>
              + Create Event
            </button>
            <span className="em-event-count">{events.length} event{events.length !== 1 ? 's' : ''}</span>
          </div>

          {showForm && (
            <div className="em-form-section">
              <div className="em-form-header">
                <h3 className="em-form-title">{editingId ? 'Edit Event' : 'New Event'}</h3>
                <button type="button" className="em-form-close" onClick={closeForm} aria-label="Close">&times;</button>
              </div>
              <form onSubmit={handleSubmit} className="em-form">
                <div className="em-field">
                  <label className="em-label">Event name</label>
                  <MotdEditor value={form.name} onChange={(v) => handleField('name', v)} />
                </div>
                <div className="em-field-row">
                  <div className="em-field">
                    <label className="em-label">Date & time</label>
                    <DatePicker
                      selected={form.time}
                      onChange={(date) => handleField('time', date)}
                      showTimeSelect
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      placeholderText="Select date and time..."
                      className="em-input"
                      wrapperClassName="em-datepicker-wrapper"
                      required
                    />
                  </div>
                  <div className="em-field">
                    <label className="em-label">Item icon</label>
                    <Select
                      options={MINECRAFT_ITEMS}
                      value={selectedItem}
                      onChange={(opt) => handleField('item', opt ? opt.value : '')}
                      isClearable
                      placeholder="Search items..."
                      components={{ Option: ItemOption, SingleValue: ItemSingleValue }}
                      styles={selectStyles}
                    />
                  </div>
                </div>
                <div className="em-field">
                  <label className="em-label">Description</label>
                  <textarea
                    className="em-input em-textarea"
                    value={form.description}
                    onChange={(e) => handleField('description', e.target.value)}
                    maxLength={1000}
                    rows={2}
                    placeholder="Optional event description..."
                  />
                </div>
                <div className="em-field-row">
                  <div className="em-field">
                    <label className="em-label">Web addon</label>
                    <select
                      className="em-input em-select"
                      value={form.webAddon}
                      onChange={(e) => handleField('webAddon', e.target.value)}
                    >
                      <option value="">None</option>
                      <option value="command-voting">Command Voting</option>
                      <option value="chunk-delete">Chunk Delete</option>
                      <option value="block-delete">Block Delete</option>
                      <option value="would-you-rather">Would You Rather</option>
                    </select>
                  </div>
                  {(form.webAddon === 'command-voting' || form.webAddon === 'chunk-delete' || form.webAddon === 'block-delete' || form.webAddon === 'would-you-rather') && (() => {
                    let config = { roundDurationSec: 300, intermissionSec: 30, borderRadius: 500 };
                    try { config = { ...config, ...JSON.parse(form.webAddonConfig) }; } catch {}
                    return (
                      <>
                        <div className="em-field">
                          <label className="em-label">Round duration (sec)</label>
                          <input
                            type="number"
                            className="em-input"
                            value={config.roundDurationSec}
                            onChange={(e) => handleField('webAddonConfig', JSON.stringify({ ...config, roundDurationSec: parseInt(e.target.value) || 300 }))}
                            min={30}
                            max={3600}
                          />
                        </div>
                        <div className="em-field">
                          <label className="em-label">Intermission (sec)</label>
                          <input
                            type="number"
                            className="em-input"
                            value={config.intermissionSec}
                            onChange={(e) => handleField('webAddonConfig', JSON.stringify({ ...config, intermissionSec: parseInt(e.target.value) || 30 }))}
                            min={5}
                            max={300}
                          />
                        </div>
                        {form.webAddon === 'chunk-delete' && (
                          <div className="em-field">
                            <label className="em-label">Border radius (blocks)</label>
                            <input
                              type="number"
                              className="em-input"
                              value={config.borderRadius}
                              onChange={(e) => handleField('webAddonConfig', JSON.stringify({ ...config, borderRadius: parseInt(e.target.value) || 500 }))}
                              min={50}
                              max={5000}
                            />
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
                {form.webAddon && (
                  <div className="em-field">
                    <label className="em-label">Event slug</label>
                    <input
                      type="text"
                      className="em-input"
                      value={form.slug}
                      onChange={(e) => handleField('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                      maxLength={50}
                      placeholder="my-event"
                    />
                    {form.slug && (
                      <span className="em-slug-preview">thetopia.net/event/{form.slug}</span>
                    )}
                  </div>
                )}
                <div className="em-toggles">
                  <label className="em-toggle">
                    <input type="checkbox" checked={form.visibility} onChange={(e) => handleField('visibility', e.target.checked)} />
                    <span className="em-toggle-track"><span className="em-toggle-thumb" /></span>
                    <span>Visible to players</span>
                  </label>
                  <label className="em-toggle">
                    <input type="checkbox" checked={form.voicechat} onChange={(e) => handleField('voicechat', e.target.checked)} />
                    <span className="em-toggle-track"><span className="em-toggle-thumb" /></span>
                    <span>Voice chat</span>
                  </label>
                </div>
                <div className="em-form-actions">
                  <button type="submit" className="em-btn-save" disabled={saving}>
                    {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create Event'}
                  </button>
                  <button type="button" className="em-btn-cancel-text" onClick={closeForm}>Cancel</button>
                  {editingId && (
                    <div className="em-form-delete">
                      <HoldToDeleteBtn onDelete={() => { handleDelete(editingId); closeForm(); }} disabled={deleting} />
                    </div>
                  )}
                </div>
              </form>
            </div>
          )}

          {!showForm && (loading ? (
            <div className="em-events-list">
              {[0, 1].map((i) => (
                <div key={i} className="em-event-card em-skeleton-card">
                  <div className="em-skeleton-eye" />
                  <div className="em-event-info">
                    <div className="em-skeleton-title" />
                    <div className="em-skeleton-meta">
                      <div className="em-skeleton-line em-skeleton-date" />
                      <div className="em-skeleton-line em-skeleton-server" />
                    </div>
                  </div>
                  <div className="em-event-actions">
                    <div className="em-skeleton-btn" />
                    <div className="em-skeleton-btn em-skeleton-btn-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="em-empty">
              <p>No events yet. Create your first one above.</p>
            </div>
          ) : (
            <div className="em-events-list">
              {events.map((event) => {
                const isPast = event.time && event.time < now;
                const isHidden = event.visibility === false;

                const fmtDate = event.time
                  ? new Date(event.time * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : null;
                const fmtTime = event.time
                  ? new Date(event.time * 1000).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  : null;

                return (
                  <div key={event.id} className="em-event-card">
                    <div className="em-event-info">
                      <h3 className="em-event-name">
                        {event.item && <McIcon item={event.item} size={20} />}
                        {event.name && /[<&]/.test(event.name)
                          ? <span className="em-event-name-colored">{parseSkriptColors(event.name, 0)}</span>
                          : (event.name || 'Unnamed Event')
                        }
                        {isHidden && <span className="em-badge em-badge-hidden">Hidden</span>}
                        {isPast && <span className="em-badge em-badge-past">Past</span>}
                      </h3>
                      <div className="em-event-meta">
                        {fmtDate && <span>{fmtDate} · {fmtTime}</span>}
                        {event.server && <span>Server: {event.server}</span>}
                        {event.voicechat && <span>Voice Chat</span>}
                      </div>
                      {event.description && <p className="em-event-desc">{event.description.split('/n').map((line, i, arr) => (
                        <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
                      ))}</p>}
                    </div>
                    <div className="em-event-actions">
                      <button className="em-btn-edit" onClick={() => openEditForm(event)}>Edit</button>
                      <HoldToDeleteBtn onDelete={() => handleDelete(event.id)} disabled={deleting} />
                      <button
                        className={`em-eye-btn ${isHidden ? 'em-eye-off' : ''}`}
                        onClick={() => toggleVisibility(event)}
                        title={isHidden ? 'Show to players' : 'Hide from players'}
                      >
                        {isHidden ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                    {event.webAddon && event.slug && (
                      <div className="em-live-controls">
                        <button className="em-live-btn em-live-btn-start" onClick={() => navigate(`/panel/event-management/control/${event.slug}`)}>
                          Control Panel
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          </>)}
        </div>
      </div>
    </section>
  );
}
